import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/cn';
import { useAgentActivityStore } from '@/state/agentActivityStore';
import { ActivityPanel } from './ActivityPanel';
import type { ChatMessage } from './chatStore';

export function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const activity = useAgentActivityStore((s) =>
    isUser || isSystem ? undefined : s.byMessageId[message.id],
  );
  // While streaming with no content yet, the "Thinking…" banner in ChatPanel
  // covers the wait state — don't render an empty bubble next to it.
  if (message.streaming && !message.content) return null;
  return (
    <div
      className={cn(
        'flex w-full',
        isUser ? 'justify-end' : 'justify-start',
      )}
    >
      <div
        className={cn(
          'max-w-[88%] rounded-lg border px-3 py-2 text-sm leading-relaxed',
          isUser
            ? 'border-primary/30 bg-primary text-primary-foreground'
            : isSystem
              ? 'border-dashed border-border bg-muted/40 text-muted-foreground italic'
              : 'border-border bg-card text-card-foreground',
        )}
      >
        {activity && activity.length > 0 && <ActivityPanel entries={activity} />}
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ children, ...props }) => (
              <p className="my-2 first:mt-0 last:mb-0" {...props}>
                {children}
              </p>
            ),
            ul: ({ children, ...props }) => (
              <ul className="my-2 list-disc space-y-1 pl-5" {...props}>
                {children}
              </ul>
            ),
            ol: ({ children, ...props }) => (
              <ol className="my-2 list-decimal space-y-1 pl-5" {...props}>
                {children}
              </ol>
            ),
            li: ({ children, ...props }) => <li {...props}>{children}</li>,
            h1: ({ children, ...props }) => (
              <h1 className="mb-2 mt-3 text-base font-semibold first:mt-0" {...props}>
                {children}
              </h1>
            ),
            h2: ({ children, ...props }) => (
              <h2 className="mb-2 mt-3 text-sm font-semibold first:mt-0" {...props}>
                {children}
              </h2>
            ),
            h3: ({ children, ...props }) => (
              <h3 className="mb-1 mt-3 text-sm font-medium first:mt-0" {...props}>
                {children}
              </h3>
            ),
            blockquote: ({ children, ...props }) => (
              <blockquote className="my-2 border-l-2 border-border/80 pl-3 italic" {...props}>
                {children}
              </blockquote>
            ),
            pre: ({ children, ...props }) => (
              <pre
                className="my-2 overflow-x-auto rounded-md border border-border/60 bg-muted/70 p-2"
                {...props}
              >
                {children}
              </pre>
            ),
            code: ({ className, children, ...props }) => {
              const isBlock = !!className?.includes('language-');
              return (
                <code
                  className={cn(
                    'font-mono text-[0.85em]',
                    isBlock ? 'block whitespace-pre' : 'rounded bg-muted px-1 py-0.5',
                    className,
                  )}
                  {...props}
                >
                  {children}
                </code>
              );
            },
            table: ({ children, ...props }) => (
              <div className="my-2 overflow-x-auto">
                <table className="w-full border-collapse text-left text-xs" {...props}>
                  {children}
                </table>
              </div>
            ),
            th: ({ children, ...props }) => (
              <th className="border border-border/60 bg-muted/70 px-2 py-1 font-medium" {...props}>
                {children}
              </th>
            ),
            td: ({ children, ...props }) => (
              <td className="border border-border/60 px-2 py-1 align-top" {...props}>
                {children}
              </td>
            ),
            a: ({ children, ...props }) => (
              <a className="underline decoration-dotted underline-offset-2" target="_blank" rel="noreferrer" {...props}>
                {children}
              </a>
            ),
          }}
        >
          {message.content}
        </ReactMarkdown>
      </div>
    </div>
  );
}
